import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { aiApi, handleAiError } from './api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Loader2, Sparkles, Trash2, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '../lib/logger';
import UsageNudge from './UsageNudge';

const MOODS = ['confident', 'focused', 'calm', 'frustrated', 'anxious', 'tired', 'fired up'];

function renderEntriesList({ loading, entries, onRemove }) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-foreground/60 text-sm">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading...
      </div>
    );
  }
  if (entries.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="p-6 text-center text-foreground/60">
          <BookOpen className="h-6 w-6 mx-auto mb-2 text-foreground/40" />
          <p className="text-sm">No entries yet. Your first reflection will show up here.</p>
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="space-y-3">
      {entries.map((e) => (
        <Card key={e.entry_id} className="border-border" data-testid={`entry-${e.entry_id}`}>
          <CardContent className="p-5">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="text-xs text-foreground/50">
                {new Date(e.created_at).toLocaleString()}
                {e.mood && (
                  <span className="ml-2 inline-block bg-[#004C54]/10 text-[#004C54] rounded-full px-2 py-0.5 font-semibold">{e.mood}</span>
                )}
              </div>
              <button onClick={() => onRemove(e.entry_id)} className="text-foreground/40 hover:text-destructive p-1" aria-label="Delete entry" data-testid={`delete-entry-${e.entry_id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
            <p className="text-sm leading-relaxed whitespace-pre-wrap mb-3">{e.content}</p>
            {e.reflection && (
              <div className="bg-[#F0F4F4] border border-[#004C54]/15 rounded-lg p-3 space-y-2">
                <div className="flex items-center gap-1.5">
                  <Sparkles className="h-3.5 w-3.5 text-[#004C54]" />
                  <span className="font-mono-accent uppercase tracking-wider text-[10px] text-[#004C54] font-bold">AI insight</span>
                </div>
                {e.reflection.insight && <p className="text-sm leading-relaxed">{e.reflection.insight}</p>}
                {Array.isArray(e.reflection.strengths) && e.reflection.strengths.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold text-foreground/70 mb-1">Strengths</div>
                    <ul className="text-sm space-y-0.5 list-disc list-inside">
                      {e.reflection.strengths.map((s) => <li key={s}>{s}</li>)}
                    </ul>
                  </div>
                )}
                {Array.isArray(e.reflection.prompts) && e.reflection.prompts.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold text-foreground/70 mb-1">Prompts</div>
                    <ul className="text-sm space-y-0.5 list-disc list-inside">
                      {e.reflection.prompts.map((p) => <li key={p}>{p}</li>)}
                    </ul>
                  </div>
                )}
                {e.reflection.focus_for_next_session && (
                  <div className="bg-white rounded-md p-2.5">
                    <div className="text-xs font-semibold text-[#004C54] mb-0.5">Focus next session</div>
                    <div className="text-sm">{e.reflection.focus_for_next_session}</div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function Journal() {
  const navigate = useNavigate();
  const [content, setContent] = useState('');
  const [mood, setMood] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usageTick, setUsageTick] = useState(0);

  const refresh = async () => {
    try {
      const { entries } = await aiApi.journalList();
      setEntries(entries || []);
    } catch (err) {
      logger.error('Failed to load journal entries:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const submit = async () => {
    if (!content.trim()) { toast.error('Write something first.'); return; }
    setSubmitting(true);
    try {
      await aiApi.journalReflect({ content: content.trim(), mood: mood || null });
      setContent('');
      setMood('');
      refresh();
      setUsageTick((n) => n + 1);
      toast.success('Reflection generated.');
    } catch (e) {
      handleAiError(e, toast, navigate);
      setUsageTick((n) => n + 1);
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (entry_id) => {
    try {
      await aiApi.journalDelete(entry_id);
      setEntries(entries.filter((e) => e.entry_id !== entry_id));
    } catch (e) {
      toast.error(e.message || 'Could not delete.');
    }
  };

  return (
    <div className="space-y-6" data-testid="journal-page">
      <div>
        <div className="font-mono-accent uppercase tracking-[0.2em] text-[10px] text-[#004C54] font-semibold mb-2">Journal</div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Reflect & Grow</h1>
        <p className="text-sm text-foreground/70 mt-1">Write what happened. Get an insight + prompts to deepen your growth.</p>
      </div>

      <UsageNudge kind="journal" refreshKey={usageTick} />

      <Card className="border-border">
        <CardContent className="p-5 sm:p-6 space-y-3">
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="What happened? What did you notice in your body and mind?"
            rows={5}
            className="text-[15px]"
            data-testid="journal-content"
          />
          <div>
            <label className="text-xs font-semibold text-foreground/70 mb-1.5 block">How are you feeling?</label>
            <div className="flex flex-wrap gap-2">
              {MOODS.map((m) => (
                <button
                  key={m}
                  onClick={() => setMood(mood === m ? '' : m)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                    mood === m ? 'bg-[#004C54] text-white border-[#004C54]' : 'bg-white text-foreground border-border hover:border-[#004C54]'
                  }`}
                  data-testid={`mood-${m.replace(/\s/g,'-')}`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
          <Button onClick={submit} disabled={submitting} className="bg-[#004C54] hover:bg-[#003a40] text-white h-11 px-6" data-testid="journal-submit">
            {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Reflecting...</> : <><Sparkles className="h-4 w-4 mr-2" />Reflect</>}
          </Button>
        </CardContent>
      </Card>

      <div>
        <h2 className="font-heading text-lg font-bold mb-3">Past entries</h2>
        {renderEntriesList({ loading, entries, onRemove: remove })}
      </div>
    </div>
  );
}
